import React, { useMemo } from 'react';
import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Lock } from 'phosphor-react';
import { useOptionsEntitlements } from '../../context/OptionsEntitlementsContext';
import { OptionsAnalyticsMetricInfoDialog } from './OptionsAnalyticsMetricInfoDialog';
import { formatDateLong, formatNative, formatPercent, getCurrencyDigits } from './optionsPerformanceUtils';
import { useOptionsLocale } from '../../context/OptionsLanguageContext';

const formatAxisValue = (value, asset) => formatNative(value, asset, getCurrencyDigits(asset));

const ProjectionTooltip = ({ active, payload, asset, t }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <div className="min-w-[220px] rounded-2xl border border-white/10 bg-[#020617] px-4 py-3 text-sm text-white shadow-2xl" data-testid="options-performance-projection-tooltip">
      <p className="font-semibold text-white" data-testid="options-performance-projection-tooltip-label">{row.axisLabel}</p>
      <p className="mt-2 text-white/68">{t('performance.projection.tooltipEquity')}</p>
      <p className="text-lg font-bold text-white" data-testid="options-performance-projection-tooltip-value">{formatNative(row.equity_native, asset, getCurrencyDigits(asset))}</p>
    </div>
  );
};

const MetricCell = ({ label, value, secondary = null, toneClassName = 'text-white', testId }) => (
  <div className="rounded-[18px] border border-white/10 bg-black/20 px-3 py-3" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-[0.16em] text-white/40" data-typography="label" data-testid={`${testId}-label`}>{label}</p>
    <p className={`mt-2 text-sm font-semibold tabular-nums ${toneClassName}`} data-typography="metric" data-testid={`${testId}-value`}>{value}</p>
    {secondary ? <p className="mt-1 text-xs text-white/55" data-testid={`${testId}-secondary`}>{secondary}</p> : null}
  </div>
);

const buildInfoContent = (t) => ({
  title: t('performance.projection.infoTitle'),
  description: t('performance.projection.infoDescription'),
  sections: [
    {
      heading: t('performance.metricInfo.sections.whatIs'),
      body: t('performance.projection.infoWhatIs'),
    },
    {
      heading: t('performance.metricInfo.sections.howCalculated'),
      body: t('performance.projection.infoHowCalculated'),
      note: t('performance.projection.infoNote'),
    },
  ],
});

export const OptionsPerformanceEquityProjectionCard = ({ projection, selectedCurrency }) => {
  const { t } = useOptionsLocale();
  const info = useMemo(() => buildInfoContent(t), [t]);
  const { isPro, openUpgradeModal } = useOptionsEntitlements();

  const renderLockedOverlay = () => (
    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center p-6 text-center bg-[#09090B]/85 rounded-b-[24px] backdrop-blur-[2px]">
      <div className="mx-auto max-w-sm space-y-4">
        <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#A855F7]/10 text-[#A855F7] border border-[#A855F7]/20">
          <Lock size={22} weight="fill" />
        </div>
        <div>
          <p className="text-base font-bold text-white">Disponível no GENI PRO 💜</p>
          <p className="mt-1 text-xs text-white/55 leading-relaxed">Acesse projeções analíticas completas de evolução de patrimônio para o seu portfólio.</p>
        </div>
        <Button 
          onClick={() => openUpgradeModal("PERFORMANCE")}
          className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-bold text-xs px-5 py-2.5 rounded-xl transition-all"
        >
          DESBLOQUEAR ACESSO PRO
        </Button>
      </div>
    </div>
  );

  if (selectedCurrency === 'usd') {
    return (
      <Card className="relative border-white/10 bg-white/[0.03] text-white shadow-none" data-testid="options-performance-equity-projection-card-usd-state">
        <CardHeader className="space-y-3 pb-4">
          <div className="flex items-center gap-2 flex-wrap">
            <CardTitle className="text-2xl font-black tracking-tight text-white" data-typography="heading">{t('performance.projection.title')}</CardTitle>
            {!isPro && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#A855F7]/10 text-[#A855F7] border border-[#A855F7]/20 px-2 py-0.5 text-xs font-semibold">
                <Lock size={11} weight="fill" /> PRO
              </span>
            )}
            <OptionsAnalyticsMetricInfoDialog info={info} testId="options-performance-equity-projection-info" />
          </div>
        </CardHeader>
        <CardContent className="relative">
          <div className="rounded-2xl border border-dashed border-white/10 bg-black/20 px-5 py-8 text-center" data-testid="options-performance-equity-projection-usd-empty-state">
            <p className="text-lg font-semibold text-white" data-testid="options-performance-equity-projection-usd-empty-title">{t('performance.projection.usdUnavailableTitle')}</p>
            <p className="mx-auto mt-2 max-w-2xl text-sm text-white/60" data-testid="options-performance-equity-projection-usd-empty-description">{t('performance.projection.usdUnavailableDescription')}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const asset = projection?.asset || selectedCurrency.toUpperCase();
  const series = (projection?.projected_points || []).map((point) => ({
    ...point,
    axisLabel: point.month_index === 0 ? t('performance.projection.todayTick') : `${point.month_index}M`,
  }));
  const yValues = series.map((point) => point.equity_native).filter((value) => value !== null && value !== undefined && Number.isFinite(Number(value))).map(Number);
  const yMin = yValues.length ? Math.min(...yValues) : 0;
  const yMax = yValues.length ? Math.max(...yValues) : 1;
  const yPadding = yMin === yMax ? Math.max(Math.abs(yMax) * 0.05, 0.25) : Math.max((yMax - yMin) * 0.12, Math.abs(yMax) * 0.02);
  const ready = projection?.status === 'ready' && series.length > 1;
  const sourceWindowLabel = projection?.source_window ? t(`performance.projection.sourceWindows.${projection.source_window}`) : null;
  const sourceRangeLabel = projection?.source_start && projection?.source_end ? `${formatDateLong(projection.source_start)} → ${formatDateLong(projection.source_end)}` : null;
  const annualizedTone = (projection?.annualized_return_pct || 0) > 0 ? 'text-[#22C55E]' : (projection?.annualized_return_pct || 0) < 0 ? 'text-[#EF4444]' : 'text-white';

  return (
    <Card 
      onClick={() => { if (!isPro) openUpgradeModal("PERFORMANCE"); }}
      className={`relative border-white/10 bg-white/[0.03] text-white shadow-none ${!isPro ? 'cursor-pointer' : ''}`} 
      data-testid="options-performance-equity-projection-card"
    >
      <CardHeader className="space-y-3 pb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <CardTitle className="text-2xl font-black tracking-tight text-white" data-typography="heading" data-testid="options-performance-equity-projection-title">{t('performance.projection.title')}</CardTitle>
          {!isPro && (
            <span className="inline-flex items-center gap-1 rounded-full bg-[#A855F7]/10 text-[#A855F7] border border-[#A855F7]/20 px-2 py-0.5 text-xs font-semibold">
              <Lock size={11} weight="fill" /> PRO
            </span>
          )}
          <OptionsAnalyticsMetricInfoDialog info={info} testId="options-performance-equity-projection-info" />
        </div>
      </CardHeader>
      <CardContent className={`space-y-5 relative ${!isPro ? 'blur-[4px] pointer-events-none select-none min-h-[300px]' : ''}`}>
        {ready ? (
          <>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4" data-testid="options-performance-equity-projection-metrics-grid">
              <MetricCell label={t('performance.projection.currentEquity')} value={formatNative(projection.current_equity, asset, getCurrencyDigits(asset))} testId="options-performance-equity-projection-current-equity" />
              <MetricCell label={t('performance.projection.base')} value={sourceWindowLabel || '—'} secondary={sourceRangeLabel || undefined} testId="options-performance-equity-projection-base" />
              <MetricCell label={t('performance.projection.annualizedReturn')} value={formatPercent(projection.annualized_return_pct)} secondary={projection?.limited_history ? t('performance.projection.limitedHistoryNote').replace('{days}', String(projection?.available_history_days || projection?.source_days || 0)) : undefined} toneClassName={annualizedTone} testId="options-performance-equity-projection-annualized-return" />
              <MetricCell label={t('performance.projection.projected12m')} value={`≈ ${formatNative(projection.projected_12m_equity, asset, getCurrencyDigits(asset))}`} testId="options-performance-equity-projection-12m" />
            </div>

            <div className="h-[320px] w-full" data-testid="options-performance-equity-projection-chart-wrapper">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={series} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                  <XAxis dataKey="axisLabel" tick={{ fill: 'rgba(255,255,255,0.58)', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis domain={[yMin - yPadding, yMax + yPadding]} tick={{ fill: 'rgba(255,255,255,0.58)', fontSize: 11 }} axisLine={false} tickLine={false} width={96} tickFormatter={(value) => formatAxisValue(value, asset)} />
                  <Tooltip content={<ProjectionTooltip asset={asset} t={t} />} />
                  <ReferenceLine x={series[0]?.axisLabel} stroke="rgba(255,255,255,0.22)" strokeDasharray="4 4" label={{ value: t('performance.projection.todayMarker'), position: 'insideTopLeft', fill: 'rgba(255,255,255,0.45)', fontSize: 11 }} />
                  <Line type="monotone" dataKey="equity_native" stroke="#F0B90B" strokeWidth={2.5} strokeDasharray="7 5" dot={{ r: 2.5, fill: '#F0B90B', strokeWidth: 0 }} activeDot={{ r: 4, fill: '#F0B90B', strokeWidth: 0 }} isAnimationActive={false} data-testid="options-performance-equity-projection-chart-line" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </>
        ) : (
          <div className="rounded-2xl border border-dashed border-white/10 bg-black/20 px-5 py-8 text-center" data-testid="options-performance-equity-projection-empty-state">
            <p className="text-lg font-semibold text-white" data-testid="options-performance-equity-projection-empty-title">{t('performance.projection.insufficientTitle')}</p>
            <p className="mx-auto mt-2 max-w-2xl text-sm text-white/60" data-testid="options-performance-equity-projection-empty-description">{t('performance.projection.insufficientDescription')}</p>
            {projection?.available_history_days ? <p className="mt-3 text-xs text-white/48" data-testid="options-performance-equity-projection-empty-days">{t('performance.projection.availableHistory').replace('{days}', String(projection.available_history_days))}</p> : null}
          </div>
        )}
      </CardContent>
      {!isPro && renderLockedOverlay()}
    </Card>
  );
};

export default OptionsPerformanceEquityProjectionCard;
